import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { X, Search, Plus, Trash2, FileText, Mail, Download, Save, FilePlus, AlertTriangle } from "lucide-react";
import DuplicateCustomerPicker from "./DuplicateCustomerPicker";
import logger from "../../utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const SIZE_OPTIONS = ["small", "medium", "large", "xlarge"];
const INVOICE_NUMBER_RE = /^[A-Z0-9][A-Z0-9-]{2,15}$/;

/**
 * Admin Invoices modal — browse every booking, then edit customer info,
 * pricing, and line items (add/remove/edit) with a live-refreshing preview
 * pane on the right. Everything persists to the same `/invoice-data`
 * endpoint used by the read/write API.
 */
const InvoicesModal = ({ open, onClose }) => {
  const [bookings, setBookings] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [search, setSearch] = useState("");
  const [previewNonce, setPreviewNonce] = useState(0);
  const [creating, setCreating] = useState(false);
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState("");

  const createManualInvoice = async (fromBookingId = null) => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/admin/bookings/manual-invoice`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fromBookingId ? { from_booking_id: fromBookingId } : {}),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success(fromBookingId ? "Invoice created — customer details copied" : "Blank invoice created — fill in the details");
      await fetchBookings();
      setSelectedId(data.id);
      await fetchInvoice(data.id);
    } catch (e) {
      toast.error("Couldn't create invoice");
    } finally {
      setCreating(false);
    }
  };

  const fetchBookings = async () => {
    setLoadingList(true);
    try {
      const res = await fetch(`${API}/api/admin/all-bookings`, { credentials: "include" });
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.bookings || [];
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setBookings(list);
    } catch (e) {
      toast.error("Couldn't load bookings");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchInvoice = async (id) => {
    setLoadingInvoice(true);
    try {
      const res = await fetch(`${API}/api/admin/bookings/${id}/invoice-data`, { credentials: "include" });
      if (!res.ok) throw new Error("load failed");
      const data = await res.json();
      // Attach a stable client-side UID to every item so React keys survive
      // reorder / add / remove without losing input focus or state.
      data.items = (data.items || []).map((it) => ({
        _uid: (globalThis.crypto?.randomUUID?.() || `it-${Date.now()}-${Math.random()}`),
        ...it,
      }));
      setInvoice(data);
      setOriginalInvoiceNumber(data.invoice_number || "");
      setPreviewNonce((n) => n + 1);
    } catch (e) {
      toast.error("Couldn't load invoice");
      setInvoice(null);
    } finally {
      setLoadingInvoice(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBookings();
      setSelectedId(null);
      setInvoice(null);
      setSearch("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) => {
      const cust = b.customer_details || {};
      const hay = [
        b.id, b.invoice_number, b.name, b.email, b.phone, b.address,
        cust.name, cust.email, cust.phone, cust.address,
        b.status, b.payment_status,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [bookings, search]);

  const totalWithFees = useMemo(() => {
    if (!invoice) return 0;
    const p = invoice.pricing || {};
    return (Number(p.base_price) || 0) + (Number(p.priority_fee) || 0) + (Number(p.equipment_fee) || 0) + (Number(p.tip_amount) || 0);
  }, [invoice]);

  // Inputs keep the raw string while typing (so "0" can be erased); numbers are coerced on save.
  const normalizePricing = (p = {}) => Object.fromEntries(
    Object.entries(p).map(([k, v]) => [k, typeof v === "string" ? (parseFloat(v) || 0) : v])
  );

  const patchField = (path, value) => {
    setInvoice((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const addItem = () => {
    setInvoice((prev) => ({
      ...prev,
      items: [...(prev.items || []), {
        _uid: (globalThis.crypto?.randomUUID?.() || `it-${Date.now()}-${Math.random()}`),
        name: "", quantity: 1, size: "medium", description: "",
      }],
    }));
  };

  const removeItem = (idx) => {
    setInvoice((prev) => ({
      ...prev,
      items: (prev.items || []).filter((_, i) => i !== idx),
    }));
  };

  const updateItem = (idx, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      items: (prev.items || []).map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    }));
  };

  const invoiceNumberChanged = !!invoice && (invoice.invoice_number || "").trim().toUpperCase() !== originalInvoiceNumber;
  const invoiceNumberInvalid = invoiceNumberChanged && !INVOICE_NUMBER_RE.test((invoice.invoice_number || "").trim().toUpperCase());

  const save = async () => {
    if (!invoice) return;
    if (invoiceNumberInvalid) {
      toast.error("Invoice # must be 3-16 letters, numbers or dashes");
      return;
    }
    if (invoiceNumberChanged) {
      const ok = window.confirm(
        `Change invoice number from #${originalInvoiceNumber} to #${invoice.invoice_number.trim().toUpperCase()}?\n\n` +
        "Any invoice already emailed to the customer will show the old number, and Venmo payments referencing the old number won't auto-match."
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/bookings/${invoice.id}/invoice-data`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: invoice.customer,
          pricing: normalizePricing(invoice.pricing),
          // Strip the client-side `_uid` before sending — backend never sees it.
          items: (invoice.items || []).map(({ _uid, ...rest }) => rest),
          pickup_date: invoice.pickup_date,
          payment_status: invoice.payment_status,
          invoice_number: (invoice.invoice_number || "").trim().toUpperCase(),
          invoice_notes: invoice.invoice_notes || "",
        }),
      });
      if (!res.ok) {
        let detail = "Save failed";
        try { detail = (await res.json()).detail || detail; } catch (parseErr) { logger.debug("invoice save: non-JSON error body", parseErr); }
        throw new Error(detail);
      }
      toast.success("Invoice updated");
      setOriginalInvoiceNumber((invoice.invoice_number || "").trim().toUpperCase());
      setPreviewNonce((n) => n + 1);
      fetchBookings(); // refresh list totals in case name/email changed
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const emailInvoice = async () => {
    if (!invoice) return;
    setEmailing(true);
    try {
      const res = await fetch(`${API}/api/admin/bookings/${invoice.id}/invoice/email`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Emailed to ${data.to_email}`);
      } else {
        toast.error(data.detail || "Send failed");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] h-[92vh] p-0 overflow-hidden flex flex-col bg-white"
        data-testid="invoices-modal"
      >
        <DialogHeader className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-600" /> Invoices
            </DialogTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-md overflow-hidden">
                <Button
                  data-testid="new-invoice-btn"
                  onClick={() => createManualInvoice()}
                  disabled={creating}
                  size="sm"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1 h-8 rounded-none"
                >
                  <FilePlus className="w-4 h-4" /> {creating ? "Creating…" : "New invoice"}
                </Button>
                <DuplicateCustomerPicker bookings={bookings} disabled={creating} onPick={(id) => createManualInvoice(id)} />
              </div>
              <button
                onClick={onClose}
                data-testid="invoices-close-btn"
                className="p-1.5 hover:bg-slate-200 rounded-md"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr_1fr] overflow-hidden">
          {/* LEFT: booking list */}
          <div className="border-r border-slate-200 flex flex-col overflow-hidden bg-slate-50">
            <div className="p-2 border-b border-slate-200 bg-white">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="invoices-search"
                  className="pl-8 h-9 text-sm"
                  placeholder="Search name, email, phone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingList ? (
                <p className="text-xs text-slate-400 p-3">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="text-xs text-slate-400 p-3">No bookings match.</p>
              ) : (
                filtered.map((b) => {
                  const cust = b.customer_details || {};
                  const name = cust.name || b.name || cust.email || b.email || "Customer";
                  const price = b.approved_price ?? b.total_price ?? b.adjusted_price ?? b.original_price ?? 0;
                  const isActive = selectedId === b.id;
                  return (
                    <button
                      key={b.id}
                      onClick={() => {
                        setSelectedId(b.id);
                        fetchInvoice(b.id);
                      }}
                      data-testid={`invoice-list-item-${b.id}`}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-cyan-50 transition-colors ${isActive ? "bg-cyan-100 border-l-4 border-l-cyan-500" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-slate-900 truncate">{name}</span>
                        <span className="text-xs font-mono text-cyan-700 whitespace-nowrap">${Number(price).toFixed(0)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <span className="text-[11px] text-slate-500 truncate">#{b.invoice_number || b.id.slice(0, 8).toUpperCase()}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${b.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{b.payment_status || "unpaid"}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* MIDDLE: editor */}
          <div className="border-r border-slate-200 flex flex-col overflow-hidden">
            {!invoice ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8 text-center">
                {loadingInvoice ? "Loading…" : "Select an invoice from the list to edit."}
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-600 mb-2">Customer</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid="inv-customer-name" placeholder="Name" value={invoice.customer.name} onChange={(e) => patchField("customer.name", e.target.value)} />
                      <Input data-testid="inv-customer-phone" placeholder="Phone" value={invoice.customer.phone} onChange={(e) => patchField("customer.phone", e.target.value)} />
                      <Input data-testid="inv-customer-email" placeholder="Email" value={invoice.customer.email} onChange={(e) => patchField("customer.email", e.target.value)} className="col-span-2" />
                      <Input data-testid="inv-customer-address" placeholder="Address" value={invoice.customer.address} onChange={(e) => patchField("customer.address", e.target.value)} className="col-span-2" />
                      <label className="text-xs text-slate-600 flex flex-col gap-1">
                        Service date
                        <Input
                          data-testid="inv-service-date"
                          type="date"
                          value={invoice.pickup_date || ""}
                          onChange={(e) => patchField("pickup_date", e.target.value)}
                        />
                      </label>
                      <label className="text-xs text-slate-600 flex flex-col gap-1">
                        Invoice #
                        <Input
                          data-testid="inv-invoice-number"
                          className={`font-mono uppercase ${invoiceNumberInvalid ? "border-red-400" : invoiceNumberChanged ? "border-amber-400" : ""}`}
                          maxLength={16}
                          value={invoice.invoice_number || ""}
                          onChange={(e) => patchField("invoice_number", e.target.value.toUpperCase())}
                        />
                      </label>
                      {invoiceNumberChanged && (
                        <div data-testid="inv-invoice-number-warning" className={`col-span-2 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] ${invoiceNumberInvalid ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>
                            {invoiceNumberInvalid
                              ? "Invoice # must be 3-16 letters, numbers or dashes."
                              : <>Changing from <b>#{originalInvoiceNumber}</b>. Emailed copies keep the old number and Venmo payments referencing it won&apos;t auto-match. You&apos;ll be asked to confirm on save.</>}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-600 mb-2">Pricing</p>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs text-slate-600 col-span-2 flex flex-col gap-1">
                        Base price ($)
                        <Input data-testid="inv-base-price" type="number" step="0.01" value={invoice.pricing.base_price ?? ""} onChange={(e) => patchField("pricing.base_price", e.target.value)} onFocus={(e) => e.target.select()} />
                      </label>
                      <label className="text-xs text-slate-600 flex flex-col gap-1">
                        Priority fee
                        <Input data-testid="inv-priority-fee" type="number" step="0.01" value={invoice.pricing.priority_fee ?? ""} onChange={(e) => patchField("pricing.priority_fee", e.target.value)} onFocus={(e) => e.target.select()} />
                      </label>
                      <label className="text-xs text-slate-600 flex flex-col gap-1">
                        Equipment fee
                        <Input data-testid="inv-equipment-fee" type="number" step="0.01" value={invoice.pricing.equipment_fee ?? ""} onChange={(e) => patchField("pricing.equipment_fee", e.target.value)} onFocus={(e) => e.target.select()} />
                      </label>
                      <label className="text-xs text-slate-600 col-span-2 flex flex-col gap-1">
                        Tip
                        <Input data-testid="inv-tip-amount" type="number" step="0.01" value={invoice.pricing.tip_amount ?? ""} onChange={(e) => patchField("pricing.tip_amount", e.target.value)} onFocus={(e) => e.target.select()} />
                      </label>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                      <span className="text-xs uppercase font-bold text-slate-500">Grand total</span>
                      <span className="text-lg font-black text-cyan-700 font-mono" data-testid="inv-grand-total">${totalWithFees.toFixed(2)}</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-600 mb-2">Watermark</p>
                    <label className="text-xs text-slate-600 flex flex-col gap-1">
                      Show &quot;PAID&quot; watermark across invoice
                      <select
                        data-testid="inv-payment-status"
                        value={invoice.payment_status || "unpaid"}
                        onChange={(e) => patchField("payment_status", e.target.value)}
                        className="h-9 text-sm border border-slate-300 rounded-md px-2 bg-white"
                      >
                        <option value="paid">✓ PAID — show diagonal watermark</option>
                        <option value="unpaid">Unpaid — no watermark</option>
                        <option value="refunded">Refunded — no watermark</option>
                        <option value="cancelled">Cancelled — no watermark</option>
                      </select>
                    </label>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-600 mb-2">Notes &amp; payment terms</p>
                    <textarea
                      data-testid="inv-notes"
                      rows={3}
                      maxLength={1000}
                      placeholder="Printed at the bottom of the invoice — e.g. “Payment due within 7 days. Thanks for keeping Flagstaff clean!”"
                      value={invoice.invoice_notes || ""}
                      onChange={(e) => patchField("invoice_notes", e.target.value)}
                      className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white resize-y focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    />
                    <p className="text-[10px] text-slate-400 text-right">{(invoice.invoice_notes || "").length}/1000</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-600">Line items</p>
                      <Button data-testid="inv-add-item-btn" size="sm" variant="outline" onClick={addItem} className="h-7 gap-1 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50">
                        <Plus className="w-3.5 h-3.5" /> Add item
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(invoice.items || []).map((it, idx) => (
                        <div key={it._uid || idx} data-testid={`inv-item-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-1.5">
                          <div className="flex items-start gap-2">
                            <Input data-testid={`inv-item-name-${idx}`} placeholder="Item name" value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} className="flex-1 h-8 text-sm" />
                            <Input data-testid={`inv-item-qty-${idx}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", parseInt(e.target.value) || 1)} className="w-16 h-8 text-sm" />
                            <select data-testid={`inv-item-size-${idx}`} value={it.size} onChange={(e) => updateItem(idx, "size", e.target.value)} className="w-24 h-8 text-sm border border-slate-300 rounded-md px-1.5 bg-white">
                              {SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button data-testid={`inv-item-remove-${idx}`} onClick={() => removeItem(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md flex-shrink-0" aria-label="Remove item">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <Input data-testid={`inv-item-desc-${idx}`} placeholder="Description (optional)" value={it.description || ""} onChange={(e) => updateItem(idx, "description", e.target.value)} className="h-7 text-xs" />
                        </div>
                      ))}
                      {(invoice.items || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic text-center py-3">No line items yet — click &quot;Add item&quot; to start.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 p-3 bg-slate-50 flex items-center gap-2 flex-wrap">
                  <Button data-testid="inv-save-btn" onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1">
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
                  </Button>
                  <Button data-testid="inv-email-btn" onClick={emailInvoice} disabled={emailing || !invoice.customer.email} variant="outline" className="gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50">
                    <Mail className="w-4 h-4" /> {emailing ? "Sending…" : "Email invoice"}
                  </Button>
                  <a data-testid="inv-pdf-btn" href={`${API}/api/admin/bookings/${invoice.id}/invoice.pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md border border-cyan-300 text-cyan-700 hover:bg-cyan-50">
                    <Download className="w-4 h-4" /> PDF
                  </a>
                </div>
              </>
            )}
          </div>

          {/* RIGHT: preview */}
          <div className="hidden md:flex flex-col overflow-hidden bg-slate-100">
            <div className="px-3 py-2 border-b border-slate-200 bg-white flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Live preview</span>
              {invoice && <span className="text-[10px] text-slate-400">Refreshes after Save</span>}
            </div>
            {invoice ? (
              <iframe
                key={`preview-${invoice.id}-${previewNonce}`}
                title="Invoice preview"
                src={`${API}/api/admin/bookings/${invoice.id}/invoice`}
                className="flex-1 bg-white"
                data-testid="invoice-preview-frame"
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select an invoice to preview.</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicesModal;
